import { activateEventBus } from './events/activateEventBus.js';
import {
	getDRAdminConfigData,
	getVehicleMmdId,
	getWalletScriptHost,
	getTheme,
	parseConfigDownPaymentPercent,
} from './helpers/index.js';
import { fetchFeatureToggles } from './integrations/featureToggles.js';
import { ddcLog } from './utils/logger.js';
import { addPreconnect, addPreload, getSafeOrigin } from './utils/resourceHints.js';
import { UDS_SCRIPT_NAME } from './global-constants.js';
import {
	createLegacyUdsLoader,
	createModernUdsLoader,
	resolveUdsPromise,
} from './utils/uds.js';

// Initialize event bus
ddcLog('Activating event bus...');
activateEventBus();

/**
 * Wait for DDC dataLayer to determine page type
 */
function waitForPageType() {
	return new Promise((resolve, reject) => {
		let checkCount = 0;
		const maxChecks = 200; // 10 seconds max with 50ms interval

		const checkInterval = setInterval(() => {
			checkCount += 1;

			// Check if DDC dataLayer exists
			const pageInfo = window.DDC?.dataLayer?.page?.pageInfo;
			if (!pageInfo) {
				if (checkCount >= maxChecks) {
					clearInterval(checkInterval);
					reject(new Error('DDC dataLayer not available'));
				}
				return;
			}

			// Check page type
			if (pageInfo.isVlp) {
				clearInterval(checkInterval);
				resolve({ pageType: 'SRP', vehicleUUID: null });
			} else if (pageInfo.isVdp) {
				// Get vehicle UUID from URL or first vehicle
				const vehicleUUID = window.DDC?.dataLayer?.vehicles?.[0]?.uuid || null;
				clearInterval(checkInterval);
				resolve({ pageType: 'VDP', vehicleUUID });
			} else {
				clearInterval(checkInterval);
				resolve({ pageType: 'OTHER', vehicleUUID: null });
			}
		}, 50); // Check every 50ms
	});
}

/**
 * Get site configuration from DDC PrivateAPI
 */
function getConfig() {
	const integrationId = 'mywallet';
	const rawConfig = window.DDC?.PrivateAPI?.integrationConfigs?.[integrationId];

	// Extract config from v2 structure { fields: {...}, urls: {...} }
	return rawConfig?.fields && typeof rawConfig.fields === 'object'
		? rawConfig.fields
		: rawConfig || {};
}

/**
 * Get vehicle inventory data
 */
function getVehicleData() {
	// Option 1: From global DDC.InvData
	if (window.DDC?.InvData && window.DDC.InvData.length > 0) {
		ddcLog('getVehicleData -> Using window.DDC.InvData, length:', window.DDC.InvData.length);
		return window.DDC.InvData;
	}

	// Option 2: On detail pages, try DDC.dataLayer.vehicles
	if (window.DDC?.dataLayer?.vehicles && window.DDC.dataLayer.vehicles.length > 0) {
		ddcLog('getVehicleData -> Using window.DDC.dataLayer.vehicles, length:', window.DDC.dataLayer.vehicles.length);
		return window.DDC.dataLayer.vehicles;
	}

	ddcLog('getVehicleData -> No vehicle data found, returning empty array');
	return [];
}

/**
 * Get dealer data
 */
function getDealerData() {
	const dataLayer = window.DDC?.dataLayer || {};
	const dealership = dataLayer.dealership || {};
	const site = dataLayer.site || {};

	if (Object.keys(dealership).length > 0) {
		const dealerCodes = {};
		if (Array.isArray(dealership.dealerCode)) {
			dealership.dealerCode.forEach(entry => Object.assign(dealerCodes, entry));
		}

		return {
			dealershipAddress1: dealership.address1 || '',
			dealershipAddress2: dealership.address2 || '',
			dealershipCity: dealership.city || '',
			dealershipCodes: dealerCodes,
			dealershipCountry: dealership.country || '',
			dealershipFranchises: site.franchises || [],
			dealershipName: dealership.dealershipName || '',
			dealershipPostalCode: dealership.postalCode || '',
			dealershipStateProvince: dealership.stateProvince || ''
		};
	}

	// Fallback: use PrivateAPI dealer content if dataLayer has not populated yet.
	if (window.DDC?.PrivateAPI?.content?.dealer) {
		return window.DDC.PrivateAPI.content.dealer;
	}

	return {
		dealershipCodes: {}
	};
}

/**
 * Get site ID
 */
function getSiteId() {
	return (
		window.DDC?.dataLayer?.site?.siteId ||
		window.DDC?.siteId ||
		'unknown'
	);
}

/**
 * Warm up the connection to the wallet script host before the DR Admin fetch
 * and script loads that will use it. On detail pages, also preload the UDS
 * script itself so it's already in flight while DR Admin resolves.
 */
function warmUpWalletScriptHost(walletScriptHost, walletScriptAssetsHost, detailPage, enableEsntialUds) {
	addPreconnect(getSafeOrigin(walletScriptHost));
	if (detailPage && !enableEsntialUds) {
		addPreload(`${walletScriptAssetsHost}/${UDS_SCRIPT_NAME}.js`, 'script');
	}
}

/**
 * Creating Pricing Update Promise
 */
const pricingUpdatePromise = new Promise((resolve) => {
	ddcLog('UDS -> Subscribing to ws-detailed-pricing/price-update');

	if (!window.DDC?.pubsub) {
		resolve(null);
		return;
	}

	let hasPricingUpdate = false;
	window.DDC.pubsub.subscribe('ws-detailed-pricing/price-update', (pricing) => {
		if (pricing && !hasPricingUpdate) {
			hasPricingUpdate = true;
			ddcLog('UDS -> ws-detailed-pricing/price-update', pricing);
			resolve(pricing);
		}
	});
});

/**
 * Run the slim initial-payment-calc path. Imports the orchestrator on-demand
 * so the heavy unified-wallet placements never enter the slim entry bundle.
 * Emits PaymentObject:Update + calcConfigUpdate:completed + calcPencilingDataUpdate:updated
 * for fast first paint in ws-track-my-offer; the full integration is loaded by the caller afterward.
 */
async function runSlim(ctx) {
	const { runInitialPaymentCalc } = await import(
		/* webpackChunkName: 'initial-payment-calc' */
		'./integrations/initialPaymentCalc/index.js'
	);
	await runInitialPaymentCalc({
		config: ctx.config,
		vehicle: ctx.vehicles[0],
		dealerData: ctx.dealerData,
		dealerSettings: ctx.drAdminConfigRoot,
		waitForPricingUpdate: ctx.waitForPricingUpdate,
		featureTogglesPromise: ctx.featureTogglesPromise,
	});
}

/**
 * Main initialization function (standalone mode).
 *
 * Does only the lightweight setup needed to decide between the slim and full
 * paths. The full path is dynamic-imported so its module graph (placements,
 * listeners, wallet-manager, etc.) is only fetched when needed.
 */
async function init(waitForPricingUpdate) {
	try {
		ddcLog('Initializing MyWallet integration (standalone mode)');

		const { InvData } = window.DDC || {};

		// Wait for page type detection via DDC dataLayer
		const { pageType, vehicleUUID: _vehicleUUID } = await waitForPageType();

		// Map pageType to legacy format
		const searchPage = pageType === 'SRP';
		const detailPage = pageType === 'VDP';
		const indexPage = pageType === 'OTHER';

		// Detect layout type
		const layoutType = window.innerWidth < 768 ? 'mobile' : 'desktop';
		const isMobile = layoutType === 'mobile';

		// Get site ID
		const siteId = getSiteId();

		// Get design/themekit
		const siteDesign = window.DDC?.dataLayer?.site?.design || {};

		// Build page data object (similar to pageEvent.payload)
		const pageData = {
			searchPage,
			detailPage,
			indexPage,
			layoutType,
			siteId,
			design: siteDesign
		};

		ddcLog('pageData:', pageData);
		ddcLog('Page classification:', { searchPage, detailPage, indexPage, isMobile, siteId });

		const isSRP = !!(searchPage && InvData);
		const isInventoryPage = !!(searchPage || detailPage);

		const config = getConfig();
		const vehicles = isInventoryPage ? getVehicleData() : [];
		const dealerData = getDealerData();
		ddcLog('Config loaded:', config);
		ddcLog('Vehicles loaded:', vehicles);

		const { useStaging, enableDealStarter, showHeaderPlacement, defaultDownPaymentPercent, enableEsntialUds = false } = config;
		const formatDownPaymentPercent = defaultDownPaymentPercent ? parseInt(defaultDownPaymentPercent, 10) / 100 : 0.2;
		const walletSubdomain = useStaging ? 'staging' : 'www';
		const walletScriptHost = getWalletScriptHost(walletSubdomain);
		const walletScriptAssetsHost = `${walletScriptHost}/assets`;
		const walletManagerHost = `${walletScriptHost}/wallet-manager`;

		warmUpWalletScriptHost(walletScriptHost, walletScriptAssetsHost, detailPage, enableEsntialUds);

		// Full integration — start download immediately so the heavy chunk is fetched
		// in parallel with getDRAdminConfigData (network call) and runSlim.
		const fullIntegrationPromise = import(
			/* webpackChunkName: 'full-integration-modern' */
			'./full-integration-modern.js'
		);

		// Get DR Admin Config data
		const drAdminResult = !enableEsntialUds ? await getDRAdminConfigData(dealerData, vehicles, config) : {};
		const {
			drAdminConfig: drAdminConfigRoot = {},
			mmdId,
			type,
		} = drAdminResult;
		const { products: drAdminProducts = {} } = drAdminConfigRoot;

		const vehicleMmdId = getVehicleMmdId(vehicles[0], dealerData);
		const dealerId = mmdId ?? vehicleMmdId;
		const featureTogglesPromise = detailPage ? fetchFeatureToggles(dealerId, config) : Promise.resolve(null);

		const theme = getTheme(pageData);
		const shouldLoadUDS = drAdminProducts[`hasWidget${type}`] && detailPage && !enableEsntialUds && !!document.querySelector('#vehicle-build-offer');
		const udsPromise = resolveUdsPromise({
			udsLoader: shouldLoadUDS ? createModernUdsLoader(config, vehicles, mmdId, waitForPricingUpdate, theme): null,
		});

		// Slim initial-payment-calc fast path:
		// Fires in parallel with the full integration — slim makes the payment API call
		// and emits PaymentObject:Update as soon as data arrives; the full integration
		// (UDS, wallet placements) loads and mounts concurrently. Whichever finishes
		// first doesn't block the other.
		// Esntial (CoxAuto) dealers are excluded: they skip getDRAdminConfigData so
		// drAdminConfigRoot would be empty and ecommerce would resolve incorrectly.
		if (detailPage && vehicles.length > 0 && !enableEsntialUds) {
			runSlim({ config, vehicles, dealerData, drAdminConfigRoot, waitForPricingUpdate, featureTogglesPromise }).catch((err) => {
				ddcLog('Standalone: Slim path error:', err);
			});
		}

		const { runFullStandaloneIntegration } = await fullIntegrationPromise;
		await runFullStandaloneIntegration({
			config,
			pageData,
			vehicles,
			drAdminProducts,
			drAdminConfigRoot,
			walletScriptAssetsHost,
			walletManagerHost,
			formatDownPaymentPercent,
			isSRP,
			isMobile,
			siteId,
			detailPage,
			indexPage,
			useStaging,
			enableDealStarter,
			showHeaderPlacement,
			featureTogglesPromise,
			udsPromise,
			theme,
		});
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Standalone: Error during integration:', error);
	}
}

// Initialize when DOM is ready
// Note: If this script is loaded with defer or at end of body, DOM will already be ready
function shouldUseNewWalletIntegration() {
	return window.location.search.includes('useNewWalletIntegration=true');
}

if (shouldUseNewWalletIntegration()) {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => init(pricingUpdatePromise));
	} else {
		init(pricingUpdatePromise);
	}
} else {
	ddcLog('Initializing MyWallet integration (legacy mode)');
	// Load the legacy integration directly at the entry point so page-load-v1 event fires correctly
	(async (APILoader, waitForPricingUpdate) => {
		const API = await APILoader.create(document.currentScript, 'mywallet');

		const { InvData } = window.DDC;
		API.subscribe('page-load-v1', async (pageEvent) => {
			try {
				const { payload: pageData } = pageEvent;
				ddcLog('pageData:', pageData);

				const { searchPage, detailPage, indexPage, layoutType, siteId } = pageData;
				const isMobile = layoutType === 'mobile';
				ddcLog('Page classification:', { searchPage, detailPage, indexPage, isMobile, siteId });

				const config = await API.utils.getConfig();
				ddcLog('Config loaded:', config);

				const { useStaging, enableDealStarter, showHeaderPlacement, enableEsntialUds = false } = config;
				const formatDownPaymentPercent = parseConfigDownPaymentPercent(config);
				const walletSubdomain = useStaging ? 'staging' : 'www';
				const walletScriptHost = getWalletScriptHost(walletSubdomain);
				const walletScriptAssetsHost = `${walletScriptHost}/assets`;
				const walletManagerHost = `${walletScriptHost}/wallet-manager`;

				warmUpWalletScriptHost(walletScriptHost, walletScriptAssetsHost, detailPage, enableEsntialUds);

				const isSRP = !!(searchPage && InvData);
				const isInventoryPage = !!(searchPage || detailPage);

				// Full legacy integration — start download immediately so the heavy chunk is fetched
				// in parallel with getVehicleData, getDealerData, getDRAdminConfigData, and runSlim.
				const fullLegacyIntegrationPromise = import(
					/* webpackChunkName: 'full-integration-legacy' */
					'./full-integration-legacy.js'
				);

				const vehicles = isInventoryPage ? await API.utils.getVehicleData() : [];
				ddcLog('Vehicles loaded:', vehicles);

				// Get dealer data for lease enablement check
				const dealerData = getDealerData();

				// Get DR Admin Config data
				const drAdminResult = !enableEsntialUds ? await getDRAdminConfigData(dealerData, vehicles, config) : {};
				const {
					drAdminConfig: drAdminConfigRoot = {},
					type,
					mmdId,
				} = drAdminResult;
				const { products: drAdminProducts = {} } = drAdminConfigRoot;

				const vehicleMmdId = getVehicleMmdId(vehicles[0], dealerData);
				const dealerId = mmdId ?? vehicleMmdId;
				const featureTogglesPromise = detailPage ? fetchFeatureToggles(dealerId, config) : Promise.resolve(null);

				const theme = getTheme(pageData);
				const shouldLoadUDS = drAdminProducts[`hasWidget${type}`] && detailPage && !enableEsntialUds
					&& !!document.querySelector('#vehicle-build-offer');
				const udsPromise = resolveUdsPromise({
					udsLoader: shouldLoadUDS ? createLegacyUdsLoader(waitForPricingUpdate, vehicles, mmdId, theme): null,
				});

				// Slim initial-payment-calc fast path: see equivalent comment in standalone init().
				// Esntial (CoxAuto) dealers are excluded — same reasoning as in standalone init().
				if (detailPage && vehicles.length > 0 && !enableEsntialUds) {
					runSlim({ config, vehicles, dealerData, drAdminConfigRoot, waitForPricingUpdate, featureTogglesPromise }).catch((err) => {
						ddcLog('Legacy: Slim path error:', err);
					});
				}

				const { runFullLegacyIntegration } = await fullLegacyIntegrationPromise;
				await runFullLegacyIntegration(API, {
					config,
					pageData,
					vehicles,
					drAdminProducts,
					drAdminConfigRoot,
					walletScriptAssetsHost,
					walletManagerHost,
					formatDownPaymentPercent,
					isSRP,
					isMobile,
					siteId,
					detailPage,
					indexPage,
					useStaging,
					enableDealStarter,
					showHeaderPlacement,
					featureTogglesPromise,
					udsPromise,
					theme,
				});
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Legacy: Error during integration:', error);
			}
		});
	})(window.DDC.APILoader, pricingUpdatePromise);
}
